import type { GameState, Researcher, ResearcherData, Anomaly, BreachedAnomaly, LocationId, GameData, GameSpeed, ResearchCompletionResult, BreachEscapeResult, ContainmentCompletionResult, AnomalyReceivedResult, InsanityResult, ActionCompletionToast, RealityBreachResult, RealityKillResult, RealityModifier, RealityModifierEffect, ActiveRealityEffect, AnomalyData, GameEndResult } from './types';

export class GameStateManager {
  private state: GameState;
  private gameData: GameData;
  private wasPausedBeforeModal: boolean = false;
  private gameSpeedBeforeModal: GameSpeed = '1x';
  private modalCount: number = 0;

  constructor(gameData: GameData, selectedScientists?: ResearcherData[]) {
    this.gameData = gameData;
    this.state = this.initializeState(selectedScientists);
  }

  private initializeState(selectedScientists?: ResearcherData[]): GameState {
    const config = this.gameData.gameConfig;
    
    // Create initial researchers from selected scientists
    const researchers: Researcher[] = [];
    
    if (selectedScientists && selectedScientists.length > 0) {
      // Use provided selected scientists
      selectedScientists.forEach((researcherData, i) => {
        // Check if researcher has Prepared ability and assign random silver bullet
        let initialSilverBullet: string | null = null;
        if (researcherData.special_rules.includes('Prepared')) {
          const silverBulletIds = [
            'silver_bullet_memetic',
            'silver_bullet_antiepistemic',
            'silver_bullet_breaching',
            'silver_bullet_hyperreal',
            'silver_bullet_dormant'
          ];
          const randomIndex = Math.floor(Math.random() * silverBulletIds.length);
          initialSilverBullet = silverBulletIds[randomIndex];
        }
        
        researchers.push({
          id: `researcher_${i}`,
          data: researcherData,
          currentSanity: researcherData.sanity,
          location: 'control_room',
          targetLocation: null,
          movementProgress: 0,
          currentAction: null,
          actionTimer: 0,
          actionTotalTime: 0,
          silverBullet: initialSilverBullet
        });
      });
    }
    // If no scientists selected, researchers array stays empty

    // Shuffle reality modifiers
    const shuffledModifiers = [...this.gameData.realityModifiers].sort(() => Math.random() - 0.5);

    // Shuffle anomaly pool
    const shuffledAnomalies = [...this.gameData.anomalies].sort(() => Math.random() - 0.5);

    // Calculate initial max chemicals based on Expert_Chemist count
    const expertChemistCount = researchers.filter(r => 
      r.data.special_rules.includes('Expert_Chemist')
    ).length;
    const initialMaxChemicals = config.max_chemicals + expertChemistCount;

    // Track initially selected researchers as recruited
    const recruitedResearcherNames = selectedScientists 
      ? selectedScientists.map(s => s.name)
      : [];

    return {
      researchers,
      anomalies: [],
      breachedAnomalies: [],
      pendingResearchCompletions: [],
      pendingBreachEscapes: [],
      pendingContainmentCompletions: [],
      pendingAnomalyReceived: [],
      pendingInsanities: [],
      pendingRealityBreaches: [],
      pendingRealityKills: [],
      pendingToasts: [],
      pendingMoveOrder: null,
      warpUnlocked: false,
      budget: config.starting_budget,
      chemicals: config.starting_chemicals,
      maxChemicals: initialMaxChemicals,
      computingAvailable: config.starting_computing,
      computingReserved: 0,
      anchoring: config.starting_anchoring,
      maxAnchoring: config.max_anchoring,
      availableRealityModifiers: shuffledModifiers,
      usedRealityModifiers: [],
      activeRealityEffects: [],
      cell4Disappeared: false,
      selectedEntity: { type: null, id: null },
      gameSpeed: 'pause',
      isPaused: true,
      tickCount: 0,
      recruitedResearcherNames,
      availableAnomalies: shuffledAnomalies,
      drawnAnomalies: [],
      gameEndState: null
    };
  }

  getState(): GameState {
    return this.state;
  }

  getResearchers(): Researcher[] {
    return this.state.researchers;
  }

  getResearcher(id: string): Researcher | undefined {
    return this.state.researchers.find(r => r.id === id);
  }

  getAnomalies(): Anomaly[] {
    return this.state.anomalies;
  }

  getAnomalyInCell(cellId: LocationId): Anomaly | undefined {
    return this.state.anomalies.find(a => a.cellId === cellId);
  }

  addAnomaly(anomaly: Anomaly): void {
    this.state.anomalies.push(anomaly);
  }

  removeAnomaly(anomaly: Anomaly): void {
    const index = this.state.anomalies.indexOf(anomaly);
    if (index > -1) {
      this.state.anomalies.splice(index, 1);
    }
  }

  updateResearcher(id: string, updates: Partial<Researcher>): void {
    const researcher = this.getResearcher(id);
    if (researcher) {
      Object.assign(researcher, updates);
    }
  }

  setSelectedEntity(type: 'researcher' | 'location' | 'anomaly' | null, id: string | LocationId | null): void {
    this.state.selectedEntity = { type, id };
  }

  getSelectedEntity() {
    return this.state.selectedEntity;
  }

  setGameSpeed(speed: 'pause' | '1x' | '2x' | '4x'): void {
    this.state.gameSpeed = speed;
    this.state.isPaused = speed === 'pause';
  }

  getGameSpeed(): 'pause' | '1x' | '2x' | '4x' {
    return this.state.gameSpeed;
  }

  setPaused(paused: boolean): void {
    this.state.isPaused = paused;
    if (paused) {
      this.state.gameSpeed = 'pause';
    }
  }

  isPaused(): boolean {
    return this.state.isPaused;
  }

  setPausedByModal(paused: boolean): void {
    if (paused) {
      // First modal opening - save current state
      if (this.modalCount === 0) {
        this.wasPausedBeforeModal = this.state.isPaused;
        this.gameSpeedBeforeModal = this.state.gameSpeed;
      }
      this.modalCount++;
      
      // Pause the game if not already paused
      if (!this.state.isPaused) {
        this.state.isPaused = true;
        this.state.gameSpeed = 'pause';
      }
    } else {
      // Modal closing - decrement counter
      if (this.modalCount > 0) {
        this.modalCount--;
      }
    }
  }

  restorePauseStateAfterModal(): GameSpeed | null {
    // Only restore if this was the last modal
    if (this.modalCount === 0) {
      // Restore the previous state
      this.state.isPaused = this.wasPausedBeforeModal;
      this.state.gameSpeed = this.gameSpeedBeforeModal;
      return this.gameSpeedBeforeModal;
    }
    return null;
  }

  incrementTick(): void {
    this.state.tickCount++;
  }

  getTickCount(): number {
    return this.state.tickCount;
  }

  addBudget(amount: number): void {
    this.state.budget = Math.max(0, this.state.budget + amount);
  }

  spendBudget(amount: number): boolean {
    if (this.state.budget >= amount) {
      this.state.budget -= amount;
      return true;
    }
    return false;
  }

  getBudget(): number {
    return this.state.budget;
  }

  addChemicals(amount: number): void {
    this.state.chemicals = Math.min(this.state.maxChemicals, this.state.chemicals + amount);
  }

  spendChemicals(amount: number): boolean {
    if (this.state.chemicals >= amount) {
      this.state.chemicals -= amount;
      return true;
    }
    return false;
  }

  getChemicals(): number {
    return this.state.chemicals;
  }

  reserveComputing(amount: number): boolean {
    if (this.state.computingAvailable >= amount) {
      this.state.computingAvailable -= amount;
      this.state.computingReserved += amount;
      return true;
    }
    return false;
  }

  releaseComputing(amount: number): void {
    this.state.computingAvailable += amount;
    this.state.computingReserved = Math.max(0, this.state.computingReserved - amount);
  }

  getComputingAvailable(): number {
    return this.state.computingAvailable;
  }

  calculateMaxChemicals(): number {
    const baseMax = this.gameData.gameConfig.max_chemicals;
    const expertChemistCount = this.state.researchers.filter(r => 
      r.data.special_rules.includes('Expert_Chemist')
    ).length;
    return baseMax + expertChemistCount;
  }

  updateMaxChemicals(): void {
    const newMax = this.calculateMaxChemicals();
    this.state.maxChemicals = newMax;
    
    // Clamp current chemicals to new max if needed
    if (this.state.chemicals > newMax) {
      this.state.chemicals = newMax;
    }
  }

  canAddResearcher(): boolean {
    return this.state.researchers.length < this.gameData.gameConfig.max_researchers;
  }

  getAvailableResearchers(): ResearcherData[] {
    // Return researchers that haven't been recruited yet
    return this.gameData.researchers.filter(
      researcher => !this.state.recruitedResearcherNames.includes(researcher.name)
    );
  }

  hasAvailableResearchers(): boolean {
    return this.getAvailableResearchers().length > 0;
  }

  getNextAvailableResearcher(): ResearcherData | null {
    const available = this.getAvailableResearchers();
    if (available.length === 0) return null;
    // Return the first available researcher
    return available[0];
  }

  addResearcher(researcherData: typeof this.gameData.researchers[0]): void {
    if (!this.canAddResearcher()) return;
    
    const id = `researcher_${this.state.researchers.length}`;
    
    // Check if researcher has Prepared ability and assign random silver bullet
    let initialSilverBullet: string | null = null;
    if (researcherData.special_rules.includes('Prepared')) {
      const silverBulletIds = [
        'silver_bullet_memetic',
        'silver_bullet_antiepistemic',
        'silver_bullet_breaching',
        'silver_bullet_hyperreal',
        'silver_bullet_dormant'
      ];
      const randomIndex = Math.floor(Math.random() * silverBulletIds.length);
      initialSilverBullet = silverBulletIds[randomIndex];
    }
    
    this.state.researchers.push({
      id,
      data: researcherData,
      currentSanity: researcherData.sanity,
      location: 'control_room',
      targetLocation: null,
      movementProgress: 0,
      currentAction: null,
      actionTimer: 0,
      actionTotalTime: 0,
      silverBullet: initialSilverBullet
    });
    
    // Mark this researcher as recruited (even if they die later, they can't be recruited again)
    if (!this.state.recruitedResearcherNames.includes(researcherData.name)) {
      this.state.recruitedResearcherNames.push(researcherData.name);
    }
    
    // Update max chemicals if Expert_Chemist was added
    this.updateMaxChemicals();
  }

  getBreachedAnomalies(): BreachedAnomaly[] {
    return this.state.breachedAnomalies;
  }

  getBreachedInCell(cellId: LocationId): BreachedAnomaly | undefined {
    return this.state.breachedAnomalies.find(b => b.breachCellId === cellId);
  }

  getBreachedFromCell(cellId: LocationId): BreachedAnomaly | undefined {
    return this.state.breachedAnomalies.find(b => b.originCellId === cellId);
  }

  addBreachedAnomaly(breached: BreachedAnomaly): void {
    this.state.breachedAnomalies.push(breached);
  }

  removeBreachedAnomaly(breached: BreachedAnomaly): void {
    const index = this.state.breachedAnomalies.indexOf(breached);
    if (index > -1) {
      this.state.breachedAnomalies.splice(index, 1);
    }
  }

  modifyAnchoring(amount: number): void {
    this.state.anchoring += amount;
    // Can go negative, no clamping
  }

  removeResearcher(id: string): void {
    const index = this.state.researchers.findIndex(r => r.id === id);
    if (index > -1) {
      this.state.researchers.splice(index, 1);
      
      // Update max chemicals if Expert_Chemist was removed
      this.updateMaxChemicals();
    }
  }

  queueResearchCompletion(result: ResearchCompletionResult): void {
    // Prevent duplicate entries for the same cell/researcher
    const isDuplicate = this.state.pendingResearchCompletions.some(
      pending => pending.cellId === result.cellId && pending.researcherId === result.researcherId
    );
    if (!isDuplicate) {
      this.state.pendingResearchCompletions.push(result);
    }
  }

  getPendingResearchCompletion(): ResearchCompletionResult | null {
    return this.state.pendingResearchCompletions[0] || null;
  }

  dequeueResearchCompletion(): void {
    this.state.pendingResearchCompletions.shift();
  }

  queueBreachEscape(result: BreachEscapeResult): void {
    // Prevent duplicate entries for the same anomaly
    const isDuplicate = this.state.pendingBreachEscapes.some(
      pending => pending.anomalyName === result.anomalyName
    );
    if (!isDuplicate) {
      this.state.pendingBreachEscapes.push(result);
    }
  }

  getPendingBreachEscape(): BreachEscapeResult | null {
    return this.state.pendingBreachEscapes[0] || null;
  }

  dequeueBreachEscape(): void {
    this.state.pendingBreachEscapes.shift();
  }

  queueContainmentCompletion(result: ContainmentCompletionResult): void {
    // Prevent duplicate entries for the same researcher
    const isDuplicate = this.state.pendingContainmentCompletions.some(
      pending => pending.researcherId === result.researcherId
    );
    if (!isDuplicate) {
      this.state.pendingContainmentCompletions.push(result);
    }
  }

  getPendingContainmentCompletion(): ContainmentCompletionResult | null {
    return this.state.pendingContainmentCompletions[0] || null;
  }

  dequeueContainmentCompletion(): void {
    this.state.pendingContainmentCompletions.shift();
  }

  queueAnomalyReceived(result: AnomalyReceivedResult): void {
    // Prevent duplicate modal entries for the same cell
    const isDuplicate = this.state.pendingAnomalyReceived.some(
      pending => pending.cellId === result.cellId
    );
    
    if (!isDuplicate) {
      this.state.pendingAnomalyReceived.push(result);
    }
  }

  getPendingAnomalyReceived(): AnomalyReceivedResult | null {
    return this.state.pendingAnomalyReceived[0] || null;
  }

  dequeueAnomalyReceived(): void {
    this.state.pendingAnomalyReceived.shift();
  }

  queueInsanity(result: InsanityResult): void {
    // Prevent duplicate entries for the same researcher
    const isDuplicate = this.state.pendingInsanities.some(
      pending => pending.researcherName === result.researcherName
    );
    if (!isDuplicate) {
      this.state.pendingInsanities.push(result);
    }
  }

  getPendingInsanity(): InsanityResult | null {
    return this.state.pendingInsanities[0] || null;
  }

  dequeueInsanity(): void {
    this.state.pendingInsanities.shift();
  }

  queueToast(toast: ActionCompletionToast): void {
    this.state.pendingToasts.push(toast);
  }

  getPendingToasts(): ActionCompletionToast[] {
    return this.state.pendingToasts;
  }

  clearPendingToasts(): void {
    this.state.pendingToasts = [];
  }

  isWarpUnlocked(): boolean {
    return this.state.warpUnlocked;
  }

  setWarpUnlocked(unlocked: boolean): void {
    this.state.warpUnlocked = unlocked;
  }

  // Reality Breach methods
  queueRealityBreach(result: RealityBreachResult): void {
    // Prevent duplicate entries for the same modifier (though same modifier could apply twice in theory,
    // usually reality breaches are distinct events)
    const isDuplicate = this.state.pendingRealityBreaches.some(
      pending => pending.modifierName === result.modifierName
    );
    if (!isDuplicate) {
      this.state.pendingRealityBreaches.push(result);
    }
  }

  getPendingRealityBreach(): RealityBreachResult | null {
    return this.state.pendingRealityBreaches[0] || null;
  }

  dequeueRealityBreach(): void {
    this.state.pendingRealityBreaches.shift();
  }

  queueRealityKill(result: RealityKillResult): void {
    // Prevent duplicate entries for the same researcher
    const isDuplicate = this.state.pendingRealityKills.some(
      pending => pending.researcherName === result.researcherName
    );
    if (!isDuplicate) {
      this.state.pendingRealityKills.push(result);
    }
  }

  getPendingRealityKill(): RealityKillResult | null {
    return this.state.pendingRealityKills[0] || null;
  }

  dequeueRealityKill(): void {
    this.state.pendingRealityKills.shift();
  }

  drawRealityModifier(): RealityModifier | null {
    if (this.state.availableRealityModifiers.length > 0) {
      const modifier = this.state.availableRealityModifiers.shift()!;
      this.state.usedRealityModifiers.push(modifier);
      return modifier;
    }
    
    // If all regular modifiers are used, use the end modifier
    if (this.gameData.realityModifiersEnd.length > 0) {
      return this.gameData.realityModifiersEnd[0];
    }
    
    return null;
  }

  addActiveRealityEffect(effect: ActiveRealityEffect): void {
    this.state.activeRealityEffects.push(effect);
  }

  getActiveRealityEffects(): ActiveRealityEffect[] {
    return this.state.activeRealityEffects;
  }

  hasActiveEffect(effectKey: keyof RealityModifierEffect): boolean {
    return this.state.activeRealityEffects.some(effect => 
      effect.effect[effectKey] !== undefined
    );
  }

  getEffectValue(effectKey: keyof RealityModifierEffect): number | boolean | undefined {
    const effect = this.state.activeRealityEffects.find(e => 
      e.effect[effectKey] !== undefined
    );
    return effect ? effect.effect[effectKey] : undefined;
  }

  setCell4Disappeared(disappeared: boolean): void {
    this.state.cell4Disappeared = disappeared;
  }

  isCell4Disappeared(): boolean {
    return this.state.cell4Disappeared;
  }

  getPendingMoveOrder(): { researcherId: string } | null {
    return this.state.pendingMoveOrder;
  }

  setPendingMoveOrder(order: { researcherId: string } | null): void {
    this.state.pendingMoveOrder = order;
  }

  restart(selectedScientists?: ResearcherData[]): void {
    // Reset all state to initial values with optional selected scientists
    this.state = this.initializeState(selectedScientists);
    
    // Reset modal-related state
    this.wasPausedBeforeModal = false;
    this.gameSpeedBeforeModal = '1x';
    this.modalCount = 0;
  }

  initializeWithScientists(selectedScientists: ResearcherData[]): void {
    // Initialize the game state with selected scientists
    this.state = this.initializeState(selectedScientists);
    
    // Reset modal-related state
    this.wasPausedBeforeModal = false;
    this.gameSpeedBeforeModal = '1x';
    this.modalCount = 0;
  }

  // Anomaly pool management
  drawAnomaly(): AnomalyData | null {
    if (this.state.availableAnomalies.length === 0) {
      return null;
    }
    const anomaly = this.state.availableAnomalies.shift()!;
    this.state.drawnAnomalies.push(anomaly);
    return anomaly;
  }

  getAvailableAnomalyCount(): number {
    return this.state.availableAnomalies.length;
  }

  // Game end state management
  setGameEndState(type: 'victory' | 'defeat', reason: 'wealth' | 'solved' | 'budget' | 'researchers'): void {
    this.state.gameEndState = { type, reason };
  }

  clearGameEndState(): void {
    this.state.gameEndState = null;
  }

  getGameEndState(): GameEndResult | null {
    return this.state.gameEndState;
  }
}


